import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { comissaoService } from '@/services/comissaoService'
import { politicaRetencaoService } from '@/services/politicaRetencaoService'
import { CurrencyField } from '@/components/common/CurrencyField'
import { NotaFiscalComissao, RegimeTributario } from '@/types/comissao'
import {
  DivergenciaRetencao,
  RetencoesSugeridas,
  SituacaoRetencaoSugerida,
  TRIBUTOS_RETENCAO,
  TributoRetencao,
} from '@/types/politicaRetencao'
import { brl } from './estadoFinanceiro'
import { codigoErro, dadosDoErro, mensagemErroNota } from './errosNotaFiscal'
import { motivoArquivoNaoLiberado } from './verificacaoArquivoNf'
import { ROTULO_REGIME } from './notaFiscalRotulos'

type Retencoes = Record<TributoRetencao, number>

const ZERADAS: Retencoes = { ISS: 0, IR: 0, INSS: 0, PIS: 0, COFINS: 0, CSLL: 0 }

const ROTULO_SITUACAO: Record<SituacaoRetencaoSugerida, string> = {
  SUGERIDA: 'Sugerida',
  NAO_RETEM: 'Não retém',
  DISPENSADA_VALOR_MINIMO: 'Dispensada (abaixo do mínimo)',
  SEM_POLITICA: 'Sem política — informe o valor',
  MUNICIPIO_NAO_INFORMADO: 'Informe o município do ISS',
}

/** A sugestão tem valor comparável (as outras situações ficam para digitação). */
const temValor = (s: SituacaoRetencaoSugerida) => s === 'SUGERIDA' || s === 'NAO_RETEM' || s === 'DISPENSADA_VALOR_MINIMO'

interface Props {
  nota: NotaFiscalComissao | null
  onFechar: () => void
  onAprovada: (nota: NotaFiscalComissao) => void
}

/**
 * "Aprovar fiscal" (AN-9 §3, Q-AN9-5; AN-PP5 §6.3, §9): REGISTRO do enquadramento e das retenções
 * confirmadas pelo Financeiro. A tela não calcula imposto, líquido nem divergência:
 *
 * - "Sugerir retenções" chama `GET …/retencoes-sugeridas` (regime, município do ISS, código de serviço)
 *   e mostra a sugestão da política LADO A LADO com o informado; preenche os campos com uma CÓPIA dos
 *   valores sugeridos (e o líquido sugerido do backend). Tributo sem política fica para digitação.
 * - Quem decide se o informado diverge da sugestão é o backend: 422
 *   `RETENCAO_DIVERGE_SUGESTAO_SEM_JUSTIFICATIVA` (com `data.divergencias`) abre a justificativa
 *   obrigatória; 409 `POLITICA_RETENCAO_INCOMPLETA` lista os tributos pendentes.
 * - A coerência `serviços − retenções = líquido` continua no backend (422 `NF_RETENCOES_INCOERENTES`).
 *
 * Sem sugestão, os campos vêm pré-preenchidos com o que a leitura do arquivo trouxe (cópia, não cálculo).
 */
const AprovarFiscalDialog: FC<Props> = ({ nota, onFechar, onAprovada }) => {
  const [regime, setRegime] = useState<RegimeTributario | ''>('')
  const [natureza, setNatureza] = useState('')
  const [codigoServico, setCodigoServico] = useState('')
  const [municipioIss, setMunicipioIss] = useState('')
  const [issRetido, setIssRetido] = useState(false)
  const [retencoes, setRetencoes] = useState<Retencoes>(ZERADAS)
  const [liquido, setLiquido] = useState(0)
  const [observacao, setObservacao] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const [sugestao, setSugestao] = useState<RetencoesSugeridas | null>(null)
  const [sugerindo, setSugerindo] = useState(false)
  const [erroSugestao, setErroSugestao] = useState<string | null>(null)
  /** Divergências que o backend apontou no 422; enquanto houver, a justificativa é obrigatória. */
  const [divergencias, setDivergencias] = useState<DivergenciaRetencao[] | null>(null)
  const [justificativa, setJustificativa] = useState('')
  const [pendentes, setPendentes] = useState<string[] | null>(null)
  /** Onda 5: arquivo em verificação/recusado — o servidor responderia 409. */
  const arquivoBloqueado = nota ? motivoArquivoNaoLiberado(nota) : null

  useEffect(() => {
    setErro(null)
    setSugestao(null)
    setErroSugestao(null)
    setDivergencias(null)
    setPendentes(null)
    setJustificativa('')
    setMunicipioIss('')
    if (!nota) return
    const e = nota.extracao?.status === 'CONCLUIDA' ? nota.extracao : null
    setRegime(e?.optanteSimples ? 'SIMPLES_NACIONAL' : '')
    setNatureza(e?.naturezaOperacao ?? '')
    setCodigoServico(e?.codigoServico ?? '')
    setIssRetido(!!e?.issRetido)
    setRetencoes({
      ISS: e?.issRetido ? (e.valorIss ?? 0) : 0,
      IR: e?.valorIr ?? 0,
      INSS: e?.valorInss ?? 0,
      PIS: e?.valorPis ?? 0,
      COFINS: e?.valorCofins ?? 0,
      CSLL: e?.valorCsll ?? 0,
    })
    setLiquido(e?.valorLiquido ?? 0)
    setObservacao('')
  }, [nota])

  const sugerir = async () => {
    if (!nota) return
    setSugerindo(true)
    setErroSugestao(null)
    try {
      const s = await politicaRetencaoService.sugerir(nota.id, {
        regime: regime || null,
        municipioIbge: municipioIss.trim() || null,
        codigoServico: codigoServico.trim() || null,
      })
      setSugestao(s)
      // Cópia dos valores do backend para os campos — o Financeiro confere e pode mudar.
      setRetencoes((prev) => {
        const nova = { ...prev }
        for (const item of s.itens) {
          if (temValor(item.situacao) && item.valorSugerido != null) nova[item.tributo] = item.valorSugerido
        }
        return nova
      })
      const iss = s.itens.find((i) => i.tributo === 'ISS')
      if (iss && temValor(iss.situacao)) setIssRetido(iss.situacao === 'SUGERIDA')
      if (s.completa) setLiquido(s.valorLiquidoSugerido)
      setDivergencias(null)
    } catch (e) {
      setErroSugestao(mensagemErroNota(e, 'Não foi possível sugerir as retenções.'))
    } finally {
      setSugerindo(false)
    }
  }

  const aprovar = async () => {
    if (!nota || !regime) return
    setSalvando(true)
    setErro(null)
    try {
      const aprovada = await comissaoService.aprovarFiscalNota(nota.id, {
        regimeTributario: regime,
        naturezaOperacao: natureza.trim() || null,
        codigoServico: codigoServico.trim() || null,
        issRetido,
        valorIssRetido: issRetido ? retencoes.ISS : 0,
        valorIrRetido: retencoes.IR,
        valorInssRetido: retencoes.INSS,
        valorPisRetido: retencoes.PIS,
        valorCofinsRetido: retencoes.COFINS,
        valorCsllRetido: retencoes.CSLL,
        valorLiquidoPagamento: liquido,
        observacao: observacao.trim() || null,
        municipioIncidenciaIss: municipioIss.trim() || null,
        justificativaDivergencia: justificativa.trim() || null,
      })
      onAprovada(aprovada)
    } catch (e) {
      const codigo = codigoErro(e)
      if (codigo === 'RETENCAO_DIVERGE_SUGESTAO_SEM_JUSTIFICATIVA') {
        const lista = dadosDoErro<{ divergencias: unknown }>(e)?.divergencias
        setDivergencias(Array.isArray(lista) ? (lista as DivergenciaRetencao[]) : [])
      }
      if (codigo === 'POLITICA_RETENCAO_INCOMPLETA') {
        const lista = dadosDoErro<{ pendentes: unknown }>(e)?.pendentes
        setPendentes(Array.isArray(lista) ? lista.map(String) : [])
      }
      setErro(mensagemErroNota(e, 'Falha ao registrar a aprovação fiscal.'))
    } finally {
      setSalvando(false)
    }
  }

  const exigeJustificativa = divergencias !== null
  const itemSugerido = (t: TributoRetencao) => sugestao?.itens.find((i) => i.tributo === t) ?? null
  const tributosVisiveis = TRIBUTOS_RETENCAO.filter((t) => t !== 'ISS' || issRetido)

  return (
    <Dialog open={nota !== null} onClose={onFechar} fullWidth maxWidth="md">
      <DialogTitle>Aprovação fiscal — {nota?.numero}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {erro && <Alert severity="error">{erro}</Alert>}
          {pendentes && pendentes.length > 0 && (
            <Alert severity="warning">Tributos sem política de retenção: {pendentes.join(', ')}.</Alert>
          )}
          {arquivoBloqueado && <Alert severity="warning">{arquivoBloqueado}</Alert>}
          <Typography variant="body2" color="textSecondary">
            Registre o enquadramento e as retenções que constam na nota. O sistema não calcula imposto:
            a sugestão vem da política de retenção vigente e quem confirma é o Financeiro; o backend
            confere se o valor dos serviços menos as retenções fecha com o líquido.
          </Typography>
          {nota?.conferencia && (
            <Typography variant="body2">
              Valor dos serviços confirmado: <strong>{brl(nota.conferencia.valorServicos)}</strong>
            </Typography>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Regime tributário"
              value={regime}
              onChange={(e) => setRegime(e.target.value as RegimeTributario)}
              fullWidth
            >
              {(Object.keys(ROTULO_REGIME) as RegimeTributario[]).map((r) => (
                <MenuItem key={r} value={r}>
                  {ROTULO_REGIME[r]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Município de incidência do ISS"
              value={municipioIss}
              onChange={(e) => setMunicipioIss(e.target.value)}
              inputProps={{ maxLength: 7 }}
              helperText="Código IBGE (7 dígitos). Sem ele o ISS fica sem sugestão."
              fullWidth
            />
          </Stack>
          <TextField label="Natureza da operação" value={natureza} onChange={(e) => setNatureza(e.target.value)} fullWidth />
          <TextField label="Código de serviço" value={codigoServico} onChange={(e) => setCodigoServico(e.target.value)} fullWidth />
          <FormControlLabel
            control={<Checkbox checked={issRetido} onChange={(e) => setIssRetido(e.target.checked)} />}
            label="ISS retido na fonte"
          />

          <Box>
            <Button variant="outlined" onClick={() => void sugerir()} disabled={sugerindo || !nota}>
              {sugerindo ? 'Consultando a política…' : 'Sugerir retenções'}
            </Button>
          </Box>
          {erroSugestao && <Alert severity="error">{erroSugestao}</Alert>}
          {sugestao && (
            <Alert severity={sugestao.completa ? 'info' : 'warning'}>
              Sugestão da política de retenção vigente em {sugestao.dataEmissao} — confira antes de aprovar.
              Total sugerido {brl(sugestao.totalSugerido)} · líquido sugerido {brl(sugestao.valorLiquidoSugerido)}.
              {!sugestao.completa && sugestao.pendentes.length > 0 && (
                <> Sem sugestão: {sugestao.pendentes.join(', ')} (informe os valores).</>
              )}
              {sugestao.exigePoliticaCompleta && !sugestao.completa && (
                <> Este ambiente exige política completa: a aprovação será recusada enquanto houver pendente.</>
              )}
            </Alert>
          )}

          <Table size="small" aria-label="Retenções">
            <TableHead>
              <TableRow>
                <TableCell>Tributo</TableCell>
                {sugestao && <TableCell>Sugestão da política</TableCell>}
                <TableCell>Informado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tributosVisiveis.map((t) => {
                const item = itemSugerido(t)
                return (
                  <TableRow key={t} data-testid={`retencao-${t}`}>
                    <TableCell sx={{ fontWeight: 600 }}>{t}</TableCell>
                    {sugestao && (
                      <TableCell>
                        {item ? (
                          <>
                            <Typography variant="body2">
                              {item.valorSugerido != null ? brl(item.valorSugerido) : '—'} · {ROTULO_SITUACAO[item.situacao]}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {item.regraAplicada}
                            </Typography>
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    )}
                    <TableCell sx={{ minWidth: 180 }}>
                      <CurrencyField
                        size="small"
                        label={`${t} retido`}
                        value={retencoes[t]}
                        onChange={(v) => setRetencoes((prev) => ({ ...prev, [t]: v }))}
                        fullWidth
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>

          <CurrencyField
            label="Valor líquido a pagar"
            value={liquido}
            onChange={setLiquido}
            helperText="O líquido que consta na nota."
            fullWidth
          />

          {exigeJustificativa && (
            <>
              <Alert severity="warning">
                O backend apontou retenção diferente da sugerida:
                {divergencias && divergencias.length > 0 && (
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {divergencias.map((d) => (
                      <li key={d.tributo}>
                        {d.tributo}: sugerido {d.sugerido} · informado {d.informado}
                      </li>
                    ))}
                  </Box>
                )}
              </Alert>
              <TextField
                label="Justificativa da divergência"
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                required
                multiline
                minRows={2}
                inputProps={{ maxLength: 2000 }}
                fullWidth
              />
            </>
          )}

          <TextField
            label="Observação"
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Voltar</Button>
        <Button
          variant="contained"
          onClick={() => void aprovar()}
          disabled={
            salvando || !regime || liquido <= 0 || !!arquivoBloqueado || (exigeJustificativa && !justificativa.trim())
          }
        >
          {salvando ? 'Registrando…' : 'Aprovar fiscal'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AprovarFiscalDialog
